/**
 * The PWA's start-up half: where the app is running, and the install event.
 * Ported from PropPay LX (lib/pwa.ts, lib/install.ts).
 *
 * Small on purpose — main.tsx imports it before the first render, so it lands
 * in the entry chunk. The install card's copy and decision tree live in
 * lib/install.ts, loaded only when the card mounts.
 *
 * ── Why the install event is caught here, and early ──
 *
 * Chromium fires `beforeinstallprompt` ONCE, early — often before React has
 * mounted — and it is gone unless something calls `preventDefault()` on it.
 * It used to be caught inside PwaInstallDialog's effect, which only mounts
 * when someone opens the account menu, so by then the event had long gone and
 * the one-tap Install button never appeared.
 */

/* ── Where the app is running ─────────────────────────────────────────── */

export type DisplayMode = "standalone" | "browser";

export function displayMode(): DisplayMode {
  if (typeof window === "undefined") return "browser";
  const standalone =
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.matchMedia?.("(display-mode: minimal-ui)").matches ||
    // iOS predates the display-mode query and still reports through this
    // non-standard flag. It is the only way to know, on an iPhone, whether the
    // app was launched from the Home Screen — the precondition for web push.
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return standalone ? "standalone" : "browser";
}

/** True on iOS/iPadOS, including iPadOS's desktop-class user agent. */
export function isIOS(ua: string = typeof navigator === "undefined" ? "" : navigator.userAgent,
  maxTouchPoints: number = typeof navigator === "undefined" ? 0 : navigator.maxTouchPoints): boolean {
  return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && maxTouchPoints > 1);
}

/* ── The one-tap path ─────────────────────────────────────────────────── */

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
let captured = false;
const listeners = new Set<() => void>();
const announce = () => listeners.forEach((l) => l());

/**
 * Catch `beforeinstallprompt` before React exists. Call once, from main.tsx.
 */
export function captureInstallPrompt(): void {
  if (typeof window === "undefined" || captured) return;
  captured = true;
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    announce();
  });
  // Installed from our button or from the browser's own menu — either way the
  // offer is spent, and the card should go rather than wait for a reload.
  window.addEventListener("appinstalled", () => {
    deferred = null;
    announce();
  });
}

export const canPromptToInstall = (): boolean => deferred !== null;

/** Subscribe to the event arriving or being spent. Returns an unsubscribe. */
export function onInstallability(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** Show the browser's own install dialog. Resolves to whether they took it. */
export async function promptToInstall(): Promise<boolean> {
  if (!deferred) return false;
  const e = deferred;
  // Spent either way: the same event cannot be prompted twice, and a button
  // that does nothing is worse than no button.
  deferred = null;
  announce();
  try {
    await e.prompt();
    const { outcome } = await e.userChoice;
    return outcome === "accepted";
  } catch {
    return false;
  }
}

/** Test seam. Never called by the app. */
export function __resetInstallForTests(): void {
  deferred = null;
  captured = false;
  listeners.clear();
}
